import { IProduct, ICMSApiResponse, BadgeImagesBySku, BadgeImageWithSku, EventEditionDetails, BadgeImage } from "../../../../common/types";
import OfflineStorage from "../../../../common/offlineStorage/OfflineStorage";
import CMSHelper, { CmsImageOptions } from "../../../../common/helpers/cms/CMSHelper";
import CMSBadgeAssetCache, { CmsBadgeAsset } from "../../../../common/helpers/cms/CMSBadgeAssetCache";
import { NETWORK_FAILURE_ERROR } from "../../../../common/constants";
import store from "../../../../redux/store";
import { getStoredColocatedShowsForEvent } from "../../../../common/utils/colocatedShowsUtils";

export const useCmsImages = () => {
  const preloadImages = async (products: IProduct[], eventEdition: EventEditionDetails, imageNames: string[]) => {
    if (!products.length) return;
    const cmsEventDetails = await CMSHelper.getCachedCmsEventDetails(eventEdition);
    if (!cmsEventDetails) {
      return;
    }

    const slots = products.flatMap(product =>
      imageNames.map(name => ({
        productUrl: CMSHelper.getCmsImageUrl(product.sku, name, eventEdition, cmsEventDetails),
        sharedUrl:
          product.rx_shared_logo_usage === 1
            ? CMSHelper.getCmsImageUrl(product.sku, name, eventEdition, cmsEventDetails, { useSharedImages: true })
            : ""
      }))
    );
    CMSBadgeAssetCache.clear(Array.from(new Set(slots.flatMap(slot => [slot.productUrl, slot.sharedUrl]).filter(Boolean))));
    const sharedUrls = Array.from(new Set(slots.map(slot => slot.sharedUrl).filter(Boolean)));
    const sharedAssets = await Promise.all(sharedUrls.map(url => CMSBadgeAssetCache.load(url)));
    const sharedByUrl = new Map(sharedUrls.map((url, index) => [url, sharedAssets[index]]));
    const productUrls = Array.from(
      new Set(slots.filter(slot => !slot.sharedUrl || !sharedByUrl.get(slot.sharedUrl)?.available).map(slot => slot.productUrl))
    );

    const productAssets = await Promise.all(productUrls.map(url => CMSBadgeAssetCache.load(url)));
    productAssets.forEach((asset, index) => checkPreloadFailure(asset, productUrls[index]!));
  };

  const checkPreloadFailure = (asset: CmsBadgeAsset, url: string) => {
    if (asset.available || asset.status === 404) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new Error(NETWORK_FAILURE_ERROR);
    }
    if (asset.status !== undefined) throw new Error(`Failed to download image from ${url}`);
    console.warn(`Image request failed and was skipped: ${url}`);
  };

  const getLogosFromCms = (products: IProduct[], eventEdition: EventEditionDetails) => preloadImages(products, eventEdition, ["logo"]);

  function groupImagesBySku(products: IProduct[], badgeImagesWithSku: BadgeImageWithSku[]) {
    let badgeImagesBySku: BadgeImagesBySku[] = [];

    for (const product of products) {
      const imagesWithSku = badgeImagesWithSku.filter(badge => badge.sku === product.sku);
      let imagesBySku: BadgeImage[] = [];
      for (const badge of imagesWithSku) {
        imagesBySku = [...imagesBySku, { name: badge.name, base64Image: badge.base64Image }];
      }
      badgeImagesBySku = [...badgeImagesBySku, { sku: product.sku, images: imagesBySku }];
    }
    return badgeImagesBySku;
  }

  const getBadgeImagesFromCms = async (products: IProduct[], eventEdition: EventEditionDetails) => {
    const eventProducts = products.filter(pr => pr.rx_event_edition_code === eventEdition.gbsCode);
    await preloadImages(eventProducts, eventEdition, ["image1", "image2", "image3"]);
  };

  const preloadBadgeImagesForEvents = async (
    products: IProduct[],
    eventEdition: EventEditionDetails,
    selectedColocatedShows?: EventEditionDetails[]
  ) => {
    const selectedShows = selectedColocatedShows ?? (await getStoredColocatedShowsForEvent(eventEdition));
    const allProducts = [...products, ...(store.getState().magento?.colocatedRegProducts || [])];
    const editions = new Map([eventEdition, ...selectedShows].map(show => [show.gbsCode, show]));
    await Promise.all(
      Array.from(editions.values()).map(show => {
        const eventProducts = allProducts.filter(product => product.rx_event_edition_code === show.gbsCode);
        return eventProducts.length ? preloadImages(eventProducts, show, ["logo", "image1", "image2", "image3"]) : undefined;
      })
    );
  };

  const getReceiptLogoUrl = async (locale: string) => {
    return CMSHelper.getReceiptLogoUrl(locale);
  };

  const getFreyaLogoUrl = (logoUrl, baseUrl) => {
    const NUMBER_OF_DIRECTORIES = 5;
    const splitLogoUrl = (logoUrl || "").split("/");
    const rmIndex = splitLogoUrl.indexOf("content");
    return `${baseUrl}/${splitLogoUrl
      .slice(rmIndex, rmIndex + NUMBER_OF_DIRECTORIES)
      .join("/")}/freya/logo.jpg/_jcr_content/renditions/original./123/logo.jpg`;
  };

  const readDataFromUrl = (callback, xhr: XMLHttpRequest) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };

  const toDataURL = (
    url: string,
    callback?: (result: string) => void,
    convertToDataUrl = false,
    ignoreNotFound = true
  ): Promise<string> => {
    const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = () => {
        const isSuccess = xhr.status >= 200 && xhr.status < 300;

        if (!isSuccess) {
          if (ignoreNotFound && xhr.status === 404) {
            console.warn(`Image not found (404): ${url}`);
            resolve("");
            return;
          }

          if (ignoreNotFound && isOffline()) {
            reject(new Error(NETWORK_FAILURE_ERROR));
            return;
          }

          reject(new Error(`Failed to download image from ${url}`));
          return;
        }

        if (!convertToDataUrl && !callback) {
          resolve("");
          return;
        }

        readDataFromUrl((result: string) => {
          if (callback) {
            callback(result);
          }
          resolve(result);
        }, xhr);
      };
      xhr.onerror = () => {
        if (ignoreNotFound && isOffline()) {
          reject(new Error(NETWORK_FAILURE_ERROR));
          return;
        }

        if (ignoreNotFound) {
          console.warn(`Image request failed and was skipped: ${url}`);
          resolve("");
          return;
        }
        reject(new Error(`Failed to download image from ${url}`));
      };
      xhr.open("GET", url);
      xhr.responseType = "blob";
      xhr.send();
    });
  };

  const fetchCmsImageUrl = async (
    productSku: string,
    imageName: string,
    eventEdition,
    badgeLocation: ICMSApiResponse | null,
    options?: CmsImageOptions
  ) => {
    const eventLogoUrl = badgeLocation?.siteDetails?.logo?.url || "";
    const domain = new URL(eventEdition.domain || "").origin;
    const url =
      (options?.useSharedImages || options?.useSharedLogo) && imageName === "logo"
        ? CMSHelper.getPortalSharedLogoUrl(eventLogoUrl, domain)
        : options?.useSharedImages
        ? CMSHelper.getPortalSharedImageUrl(eventLogoUrl, domain, imageName)
        : CMSHelper.getPortalImageUrl(eventLogoUrl, domain, imageName, productSku);

    return url;
  };

  const fetchEventLogo = async (eventEdition: EventEditionDetails) => {
    if (!eventEdition.domain) {
      return;
    }
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    const logoPath = cmsEventDetails?.siteDetails?.logo.url;
    const domain = new URL(eventEdition.domain || "").origin;
    const logoUrl = getFreyaLogoUrl(logoPath, domain);
    const base64Image = await toDataURL(logoUrl, undefined, true, true);
    if (!base64Image || !Boolean(["png", "jpeg"].find(format => base64Image.toLowerCase().includes(`data:image/${format};`)))) return "";
    return OfflineStorage.addEventLogoToStorage(base64Image);
  };

  const _forTestsOnly = { groupImagesBySku, fetchCmsImageUrl, toDataURL, readDataFromUrl };

  return { getLogosFromCms, getBadgeImagesFromCms, preloadBadgeImagesForEvents, fetchEventLogo, _forTestsOnly, getReceiptLogoUrl };
};
